import { Avatar, Box, Grid, Skeleton, Typography } from "@mui/material";
import { MprcJoinComponents, MprcRequestErrorReport, MprcTypographyOnDemand } from "components";
import { TrainingMeetingItemDto } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { LmsTrainingDefaultThumbnailUrl } from "views/lms/components/LmsTypeImage";
import { ActionButtonMeeting } from "views/lms/student/EnrollmentView/Course/ContentComponents/meeting/TrainingMeetingActions";
import { TrainingMeetingDataBox, meetingLocationName } from "views/lms/student/EnrollmentView/Course/ContentComponents/meeting/TrainingMeetingDataBox";
import { TrainingInteractions } from "views/lms/student/EnrollmentView/Shared/TrainingInteractions";
import { useEnrollmentViewContext } from "../../..";

interface IProps {
    componentId: string;
}

/////////////////////////////////////////////////////////////////////////////////////

export const MeetingHeaderContent = (props: IProps) => {

    const { componentId } = props;
    const { enrollment, onReload } = useEnrollmentViewContext();

    const api = useCoreApi();
    const { t } = useTranslation("lms");

    const [loading, setLoading] = useState<boolean>(true);
    const [meeting, setMeeting] = useState<TrainingMeetingItemDto>();
    const [viewError, setViewError] = useState<any>();

    ///
    const subtitleInfo = useCallback((meeting: TrainingMeetingItemDto): React.ReactNode[] => {
        var items: React.ReactNode[] = [];

        if (meeting.enrollmentData?.classroomName)
            items.push(<Typography variant="caption">{meeting.enrollmentData?.classroomName}</Typography>);

        if (meeting.finished) {
            const period = Formaters.performaFullDateRange(meeting.startDate!, meeting.endDate!, { replaceClosestDays: false });

            items.push(
                <Typography variant="caption">
                    {`${t("Student.EnrollmentView.Content.Meeting.HeldInText")} ${period}`}
                </Typography>
            );
        }

        var location = meetingLocationName(meeting.meetingType, meeting.locationName, t);
        if (location && location.length > 0)
            items.push(<Typography variant="caption">{location}</Typography>);

        return items;
    }, []);

    /////////////////////////////////////////////////////////////////////////////////////
    /// EFFECT'S
    /////////////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        async function getData() {
            try {
                setLoading(true);
                const response = await api.v1.enrollment.getMeetingItem({
                    componentId,
                    enrollmentId: enrollment.isPreview ? null : enrollment.enrollmentId,
                    classroomId: enrollment.isPreview ? enrollment.classroomId : null
                });

                setMeeting(response.data);
            } catch (error) {
                setViewError(error);
            }
            finally {
                setLoading(false);
            }
        }

        getData();
    }, [enrollment, componentId]);

    /////////////////////////////////////////////////////////////////////////////////////

    ///
    const RenderPendingHeader = (
        <>
            {meeting &&
                <Grid mt={1}>
                    <Box
                        display="grid"
                        gridTemplateColumns="min-content auto"
                        columnGap={2}
                        rowGap={1}
                    >
                        <Box>
                            <Avatar
                                variant="rounded"
                                sx={{ width: 220, height: 124 }}
                                src={LmsTrainingDefaultThumbnailUrl(enrollment.trainingContentType, enrollment.trainingThumbnailUrl)}
                            />
                        </Box>
                        <Box>
                            <TrainingMeetingDataBox meeting={meeting} />
                        </Box>
                    </Box>
                </Grid>
            }
        </>
    );

    ///
    const RenderFinishedHeader = (
        <>
            {meeting &&
                <>
                    <Grid>
                        <Grid>
                            <MprcJoinComponents components={subtitleInfo(meeting)} separator="&nbsp;&bull;&nbsp;" />
                        </Grid>
                    </Grid>
                    <Grid>
                        <Box
                            display="grid"
                            gridTemplateColumns="min-content auto"
                            columnGap={2}
                            rowGap={1}
                        >
                            <Box>
                                <Avatar
                                    variant="rounded"
                                    sx={{ width: 220, height: 124 }}
                                    src={LmsTrainingDefaultThumbnailUrl(enrollment.trainingContentType, enrollment.trainingThumbnailUrl)}
                                />
                            </Box>
                            <Box>
                                <TrainingMeetingDataBox meeting={meeting} enrollment={enrollment} />
                            </Box>
                        </Box>
                    </Grid>
                </>
            }
        </>
    );

    /////////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    /////////////////////////////////////////////////////////////////////////////////////

    return (
        <>
            {loading
                ? <Skeleton variant="rectangular" width="100%" height={"200px"} />
                :
                viewError
                    ? <MprcRequestErrorReport error={viewError} />
                    :
                    meeting &&
                    <Grid container direction="column" spacing={1}>
                        {meeting.finished ? RenderFinishedHeader : RenderPendingHeader}
                        <Grid>
                            <MprcTypographyOnDemand variant="h6" maxRows={4}>{meeting.description}</MprcTypographyOnDemand>
                        </Grid>
                        <Grid>
                            <Grid container>
                                <Grid>
                                    <ActionButtonMeeting meeting={meeting} enrollment={enrollment} onReload={onReload} />
                                </Grid>
                                <Grid size="grow">
                                    <TrainingInteractions enrollment={enrollment} readOnly={enrollment.isPreview} />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
            }
        </>
    );
};
